import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, ArrowRight } from "lucide-react";
import { CONTENT } from "../data/content";

const cardReveal = {
    hidden: { opacity: 0, y: 60 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.9, delay: (i % 2) * 0.12, ease: [0.16, 1, 0.3, 1] },
    }),
};

const Player = ({ project }) => {
    if (project.youtubeId) {
        return (
            <iframe
                className="h-full w-full"
                src={`https://www.youtube-nocookie.com/embed/${project.youtubeId}?playsinline=1&rel=0`}
                title={`${project.title} — video player`}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                data-testid={`work-video-${project.slug}`}
            />
        );
    }
    return (
        <video
            src={project.videoSrc}
            poster={project.still}
            controls
            autoPlay
            className="h-full w-full object-cover"
            data-testid={`work-video-${project.slug}`}
        />
    );
};

const ProjectOverlay = ({ project, onClose }) => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        className="fixed inset-0 z-[80] overflow-y-auto bg-black/95"
        data-testid="project-overlay"
    >
        <button
            onClick={onClose}
            data-testid="project-overlay-close"
            aria-label="Close project"
            className="fixed right-6 top-6 z-10 flex h-12 w-12 items-center justify-center border border-white/30 text-white transition-colors duration-300 hover:border-[#E50914] hover:bg-[#E50914]"
        >
            <X className="h-5 w-5" />
        </button>
        <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto max-w-5xl px-6 py-24"
        >
            <div className="aspect-video w-full overflow-hidden bg-[#0F0F0F]">
                {project.pending ? (
                    <img src={project.still} alt={`${project.title} — film still`} className="film-frame h-full w-full object-cover opacity-60" />
                ) : (
                    <Player project={project} />
                )}
            </div>
            <div className="mt-10 flex flex-col gap-4 border-t border-white/10 pt-8 md:flex-row md:items-baseline md:justify-between">
                <h3 className="font-display text-5xl tracking-wide text-white md:text-6xl">{project.title}</h3>
                <p className="font-body text-xs uppercase tracking-[0.3em] text-zinc-500">
                    {project.category}
                    {project.year ? ` — ${project.year}` : ""}
                </p>
            </div>
            <p className="mt-8 max-w-2xl font-body text-base font-light leading-relaxed text-zinc-300" data-testid="project-description">
                {project.description}
            </p>
            <div className="mt-12 grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2">
                {project.credits.map((credit) => (
                    <div key={credit.role} className="bg-black p-6">
                        <p className="font-body text-[10px] uppercase tracking-[0.3em] text-zinc-500">{credit.role}</p>
                        <p className="mt-2 font-body text-sm text-white">{credit.name}</p>
                    </div>
                ))}
            </div>
            {project.awards && project.awards.length > 0 && (
                <div className="mt-12" data-testid="project-awards">
                    <p className="font-body text-[10px] uppercase tracking-[0.35em] text-zinc-500">Awards & Recognition</p>
                    <div className="mt-4 divide-y divide-white/10 border-y border-white/10">
                        {project.awards.map((award) => (
                            <div key={award.name} className="flex items-baseline justify-between gap-4 py-4">
                                <p className="font-display text-xl tracking-wide text-white">{award.name}</p>
                                <p className="font-body text-xs uppercase tracking-[0.25em] text-zinc-500">
                                    {award.event} — {award.year}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </motion.div>
    </motion.div>
);

const ProjectCard = ({ project, index, playing, onPlay, onDetails }) => (
    <motion.article
        custom={index}
        variants={cardReveal}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        className={`group ${project.span}`}
        data-testid={`work-card-${project.slug}`}
    >
        <div className={`relative overflow-hidden bg-[#0F0F0F] ${project.ratio}`}>
            {playing && !project.pending ? (
                <Player project={project} />
            ) : project.pending ? (
                <div className="relative h-full w-full" data-testid={`work-coming-soon-${project.slug}`}>
                    <img
                        src={project.still}
                        alt={`${project.title} — film still`}
                        loading="lazy"
                        className="film-frame h-full w-full object-cover opacity-60"
                    />
                    <span className="absolute inset-0 flex items-center justify-center">
                        <span className="border border-white/25 bg-black/50 px-5 py-2 font-body text-[10px] uppercase tracking-[0.4em] text-zinc-300 backdrop-blur-sm">
                            Coming Soon
                        </span>
                    </span>
                </div>
            ) : (
                <button
                    onClick={onPlay}
                    data-testid={`work-play-${project.slug}`}
                    className="block h-full w-full text-left"
                    aria-label={`Play ${project.title}`}
                >
                    <img
                        src={project.still}
                        alt={`${project.title} — film still`}
                        loading="lazy"
                        className="film-frame h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <span className="absolute inset-0 bg-black/30 transition-opacity duration-500 group-hover:opacity-0" />
                    <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                        <span className="flex h-20 w-20 items-center justify-center rounded-full border border-white/40 bg-black/40 backdrop-blur-sm transition-colors duration-300 group-hover:border-[#E50914] group-hover:bg-[#E50914]">
                            <Play className="h-6 w-6 fill-white text-white" />
                        </span>
                    </span>
                </button>
            )}
        </div>
        <div className="mt-4 flex items-baseline justify-between gap-4 border-t border-white/10 pt-4">
            <div className="flex items-baseline gap-4">
                <span className="font-body text-xs text-zinc-500">{String(index + 1).padStart(2, "0")}</span>
                <h3 className="font-display text-2xl tracking-wide text-white transition-colors duration-300 group-hover:text-[#E50914] md:text-3xl">
                    {project.title}
                </h3>
            </div>
            <div className="flex items-center gap-6">
                <p className="hidden whitespace-nowrap font-body text-xs uppercase tracking-[0.25em] text-zinc-500 sm:block">
                    {project.category}
                    {project.year ? ` — ${project.year}` : ""}
                </p>
                <button
                    onClick={onDetails}
                    data-testid={`work-details-${project.slug}`}
                    className="flex items-center gap-2 whitespace-nowrap font-body text-[10px] uppercase tracking-[0.3em] text-zinc-400 transition-colors duration-300 hover:text-[#E50914]"
                    aria-label={`View ${project.title} details`}
                >
                    Details <ArrowRight className="h-3 w-3" />
                </button>
            </div>
        </div>
    </motion.article>
);

export const Work = () => {
    const [playingSlug, setPlayingSlug] = useState(null);
    const [detailsSlug, setDetailsSlug] = useState(null);
    const years = CONTENT.projects.map((p) => parseInt(p.year, 10)).filter(Boolean);
    const detailsProject = CONTENT.projects.find((p) => p.slug === detailsSlug);

    return (
        <section id="work" className="px-6 py-28 md:px-12 md:py-40" data-testid="work-section">
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="mb-16 flex items-end justify-between md:mb-24"
            >
                <h2 className="font-display text-6xl leading-none text-white md:text-8xl">
                    Selected <span className="text-outline">Work</span>
                </h2>
                <p className="hidden font-body text-xs uppercase tracking-[0.3em] text-zinc-500 md:block">
                    {CONTENT.projects.length} Projects
                    {years.length ? ` — ${Math.min(...years)}/${Math.max(...years)}` : ""}
                </p>
            </motion.div>
            <div className="grid grid-cols-1 gap-x-8 gap-y-16 md:grid-cols-12 md:gap-y-24">
                {CONTENT.projects.map((project, i) => (
                    <ProjectCard
                        key={project.slug}
                        project={project}
                        index={i}
                        playing={playingSlug === project.slug}
                        onPlay={() => setPlayingSlug(project.slug)}
                        onDetails={() => setDetailsSlug(project.slug)}
                    />
                ))}
            </div>
            <AnimatePresence>
                {detailsProject && <ProjectOverlay project={detailsProject} onClose={() => setDetailsSlug(null)} />}
            </AnimatePresence>
        </section>
    );
};
